import axios, { AxiosError } from 'axios';
import cookie from 'cookie';
import { RecommendationsResults } from '@algolia/recommend';
import { GetServerSidePropsContext } from 'next';
import { SessionState } from '../types/state/reducers/authReducersTypes';
import { PagedResponse } from '../types/api/Http';
import { CollectionResponse, EssentialsCatalogTotalsResponse } from '../types/api/CollectionsIndexTypes';
import { CategoryResponse } from '../types/api/CategoriesIndexTypes';
import { CollectionsIndexResponse, CollectionParams } from '../types/api/CollectionsIndexTypes';

import { BROWSER_FINGERPRINT_COOKIE_NAME } from '../constants/utils';
import { ENV } from '../constants/environments';
import { LIBRARY_IDS } from '../constants/api';
const { LIBRARY_ID_2D } = LIBRARY_IDS;
import cache from './redis';
import errorRedirectHandler from './errorRedirectHandler';
import productsHelpers from './productHelpers';

export async function getEssentialsTotals(): Promise<EssentialsCatalogTotalsResponse> {
  const apiUrl: string = `${ENV.api.baseURL}`;
  const url = `${apiUrl}/products/essentials_catalog_counts`;

  try {
    const getTotals = async () => {
      const response = await axios.get(url);
      return response.data;
    };
    const totalsResponse = await getTotals();
    return totalsResponse;
  } catch (error) {
    console.error('Error fetching totals:', error);
    return { total_2D: 0, total_3D: 0, full_total: 0 };
  }
}

export async function fetchEssentialsCatalogData(
  ctx: GetServerSidePropsContext,
  session: SessionState,
  libraryId: number
) {
  const apiUrl: string = `${ENV.api.baseURL}`;
  const { fetchTrendingCollections } = productsHelpers;

  try {
    const getCollections = async (
      collectionsUrl: string,
      collectionParams: CollectionParams
    ): Promise<CollectionsIndexResponse> => {
      if (process.env.USE_V2_PRODUCT_ENDPOINTS === 'true' && collectionParams && collectionParams.search) {
        collectionParams.search_query = collectionParams.search;
        delete collectionParams.search;
        collectionParams.search_query = `‘${collectionParams.search_query}’`;
      }
      const collectionsRes = await axios.get(collectionsUrl, {
        params: collectionParams,
        headers: {
          authorization: session?.user?.token
        }
      });

      return collectionsRes?.data;
    };
    // Fetch categories for current library
    const categoriesUrl = `${apiUrl}/categories?limit=50&library_id=${libraryId}`;
    const cacheKey = libraryId === LIBRARY_ID_2D ? 'avfx_categories_all' : 'avfx_3d_categories_all';

    const req = ctx.req;
    const cookies = cookie.parse(req?.headers?.cookie || '');
    const userToken = cookies[BROWSER_FINGERPRINT_COOKIE_NAME] || '';

    const defaultParams = {
      desc: false,
      user_token: userToken,
      ...ctx.query,
    };

    const collection2DParams = {
      library_id: 1,
      ...defaultParams,
    };

    const collection3DParams = {
      library_id: 3,
      ...defaultParams,
    };

    const collectionUrl = process.env.USE_V2_PRODUCT_ENDPOINTS ? `${apiUrl}/collections`.replace('v1', 'v2') : `${apiUrl}/products/essentials_catalog`;

    const params = libraryId === LIBRARY_ID_2D ? collection2DParams : collection3DParams;
    if (process.env.USE_V2_PRODUCT_ENDPOINTS) params['essentials_catalog'] = true;

    const getCategoriesPromise = cache(cacheKey, 3600, async () => {
      const categoriesRes = await axios.get(categoriesUrl);
      return categoriesRes?.data?.data;
    });
    // Get initial collections for current library (for pagination/display)
    const getCollectionsPromise = getCollections(
      collectionUrl,
      params,
    );
    const getTotalsPromise = getEssentialsTotals();
    const getTrendingCollectionsPromise = fetchTrendingCollections({ essentialsCatalog: true });

    let categories: Array<CategoryResponse>;
    let currentLibraryCollections: PagedResponse<CollectionResponse>;
    let totalsResponse: EssentialsCatalogTotalsResponse;
    let trendingRes: Array<RecommendationsResults>;
    let promisesError: AxiosError;

    const shouldFetchTrendingCollections = libraryId === LIBRARY_ID_2D;

    const promises: Array<Promise<any>> = [getCategoriesPromise, getCollectionsPromise, getTotalsPromise];
    if (shouldFetchTrendingCollections) promises.push(getTrendingCollectionsPromise);

    await Promise.all(promises)
      .then((response: Array<any>) => {
        categories = response[0];
        currentLibraryCollections = response[1];
        totalsResponse = response[2];
        if (shouldFetchTrendingCollections)
          trendingRes = response[3];
      })
      .catch((error) => {
        promisesError = error;
      });

    if (promisesError) {
      console.log(promisesError)
      return errorRedirectHandler(promisesError);
    }

    return {
      props: {
        categories,
        query_id: currentLibraryCollections.query_id,
        collections: currentLibraryCollections?.data,
        totalCollections: currentLibraryCollections.total_count,
        totalPages: currentLibraryCollections.total_pages,
        currentPage: currentLibraryCollections.page,
        collectionParams: libraryId === LIBRARY_ID_2D ? collection2DParams : collection3DParams,
        searchResultsCount: currentLibraryCollections.total_count,
        category: {
          name: 'Essentials Catalog',
          header_image: '/images/collections-default.jpg',
          keywords: [],
          slug: 'essentials_catalog'
        },
        total2D: totalsResponse.total_2D || null,
        total3D: totalsResponse.total_3D || null,
        fullTotal: totalsResponse.full_total || null,
        trendingCollections: trendingRes || null,
      }
    };
  } catch (error) {
    return errorRedirectHandler(error);
  }
}
